import {camelizeKeys} from 'humps';
import {noop} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';

import {useBrandFeatures} from '../../../../provider/brand-features/brand-features-hook';
import {useReviewRealtimePresence} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {ReviewPresenceActionEnum} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {fetchReviewsReply} from '../../../../service/reviews/reviews-reply';
import {ReviewDataChildType} from '../../../../service/reviews/reviews-type';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';

import {
    BulkAiGenerationSkipReasonEnum,
    BulkAiRunContextType,
    countBulkAiSkipReasons,
    getBulkAiRunSkipReason,
    isBulkAiSendAllowed,
} from './bulk-ai-generation-helper';
import {ReplySourceTypeEnum} from './review-list/review/reply-form/ai-review-generator/ai-review-generator-button-const';
import {ReviewAiSuggestErrorReasonEnum} from './review-list/review/reply-form/new-neuralink-hook';
import {ReviewDataWithCatalogType} from './review-list/review-list-type';

/**
 * How many generations may be in flight at once. The backend streams every suggestion through its
 * own Centrifugo channel, so a full page of 100 reviews would otherwise open 100 parallel requests.
 */
const GENERATION_CONCURRENCY = 5;

export enum BulkAiGenerationModeEnum {
    Generate = 'generate',
    GenerateAndSend = 'generateAndSend',
}

type BulkAiGenerationProgressType = {
    mode: BulkAiGenerationModeEnum;
    processedCount: number;
    totalCount: number;
};

type BulkAiGenerationSummaryType = {
    mode: BulkAiGenerationModeEnum;
    generatedCount: number;
    sentCount: number;
    errorCount: number;
    noLicenceCount: number;
    isCancelled: boolean;
    skippedReasons: Array<{reason: BulkAiGenerationSkipReasonEnum; count: number}>;
};

type BulkAiGenerationHookType = {
    progress: BulkAiGenerationProgressType | null;
    summary: BulkAiGenerationSummaryType | null;
    isRunning: boolean;
    hasFailedReviews: boolean;
    start: (reviews: Array<ReviewDataWithCatalogType>, mode: BulkAiGenerationModeEnum) => void;
    getEligibleCount: (reviews: Array<ReviewDataWithCatalogType>, mode: BulkAiGenerationModeEnum) => number;
    retryFailed: () => void;
    cancel: () => void;
    reset: () => void;
};

type BulkAiRunStateType = {
    mode: BulkAiGenerationModeEnum;
    queue: Array<ReviewDataWithCatalogType>;
    failedReviews: Array<ReviewDataWithCatalogType>;
    activeCount: number;
    processedCount: number;
    totalCount: number;
    generatedCount: number;
    sentCount: number;
    noLicenceCount: number;
    isCancelled: boolean;
    skippedReasons: BulkAiGenerationSummaryType['skippedReasons'];
};

function createInitialRunState(): BulkAiRunStateType {
    return {
        mode: BulkAiGenerationModeEnum.Generate,
        queue: [],
        failedReviews: [],
        activeCount: 0,
        processedCount: 0,
        totalCount: 0,
        generatedCount: 0,
        sentCount: 0,
        noLicenceCount: 0,
        isCancelled: false,
        skippedReasons: [],
    };
}

type BulkAiRunContextHookType = {
    getRunContext: (mode: BulkAiGenerationModeEnum) => BulkAiRunContextType;
    getEligibleCount: (reviews: Array<ReviewDataWithCatalogType>, mode: BulkAiGenerationModeEnum) => number;
    rememberGeneratedAnswer: (reviewId: number) => void;
};

function useBulkAiRunContext(): BulkAiRunContextHookType {
    const {getReviewAnswer} = useReviewStateHook();
    const {featuresByBrand} = useBrandFeatures();

    // The text each bulk generation left in a reply form. A draft that still equals it is ours to
    // regenerate; anything else was typed by the user and must not be overwritten.
    const generatedAnswerByReviewIdRef = useRef<Record<number, string>>({});

    const getRunContext = useCallback(
        (mode: BulkAiGenerationModeEnum): BulkAiRunContextType => ({
            featuresByBrand,
            isSending: mode === BulkAiGenerationModeEnum.GenerateAndSend,
            hasDraft: (reviewId) => {
                const answer = getReviewAnswer(reviewId);

                return answer.trim().length > 0 && answer !== generatedAnswerByReviewIdRef.current[reviewId];
            },
        }),
        [featuresByBrand, getReviewAnswer]
    );

    const getEligibleCount = useCallback(
        (reviews: Array<ReviewDataWithCatalogType>, mode: BulkAiGenerationModeEnum) => {
            const context = getRunContext(mode);

            return reviews.filter((review) => getBulkAiRunSkipReason(review, context) === null).length;
        },
        [getRunContext]
    );

    const rememberGeneratedAnswer = useCallback(
        (reviewId: number) => {
            generatedAnswerByReviewIdRef.current[reviewId] = getReviewAnswer(reviewId);
        },
        [getReviewAnswer]
    );

    return {getRunContext, getEligibleCount, rememberGeneratedAnswer};
}

export function useBulkAiGeneration(): BulkAiGenerationHookType {
    const {requestAIGenerate, setReviewAnswer, getReviewAnswer, refreshReviewWithAnswer, reviews} =
        useReviewStateHook();
    const {stopActivity} = useReviewRealtimePresence();
    const {featuresByBrand} = useBrandFeatures();
    const {getRunContext, getEligibleCount, rememberGeneratedAnswer} = useBulkAiRunContext();

    const [progress, setProgress] = useState<BulkAiGenerationProgressType | null>(null);
    const [summary, setSummary] = useState<BulkAiGenerationSummaryType | null>(null);

    const runRef = useRef<BulkAiRunStateType>(createInitialRunState());

    // `pump` and `runReview` feed each other, so the queue is reached through a ref to keep either
    // one from closing over a stale version of the other.
    const pumpRef = useRef<VoidFunction>(noop);

    const publishProgress = useCallback(() => {
        const {mode, processedCount, totalCount} = runRef.current;

        setProgress({mode, processedCount, totalCount});
    }, []);

    const finalize = useCallback(() => {
        const {mode, generatedCount, sentCount, noLicenceCount, failedReviews, isCancelled, skippedReasons} =
            runRef.current;

        setProgress(null);
        setSummary({
            mode,
            generatedCount,
            sentCount,
            errorCount: failedReviews.length,
            noLicenceCount,
            isCancelled,
            skippedReasons,
        });
    }, []);

    const runReview = useCallback(
        (review: ReviewDataWithCatalogType) => {
            runRef.current.activeCount += 1;

            function complete() {
                runRef.current.activeCount -= 1;
                runRef.current.processedCount += 1;
                publishProgress();
                pumpRef.current();
            }

            // A missing licence is counted apart and kept out of `failedReviews`: retrying it would
            // just hit the same 400, so it must not feed "Retry failed" either.
            function fail(reason: ReviewAiSuggestErrorReasonEnum = ReviewAiSuggestErrorReasonEnum.Unknown) {
                if (reason === ReviewAiSuggestErrorReasonEnum.NoLicence) {
                    runRef.current.noLicenceCount += 1;
                } else {
                    runRef.current.failedReviews = [...runRef.current.failedReviews, review];
                }

                complete();
            }

            async function send() {
                const comment = getReviewAnswer(review.id).trim();

                if (!comment || !isBulkAiSendAllowed(review, featuresByBrand)) {
                    fail();

                    return;
                }

                const reply = await fetchReviewsReply(review.id, {
                    comment,
                    replySourceType: ReplySourceTypeEnum.Neuronetwork,
                }).catch(() => null);

                if (!reply) {
                    // The generated text stays in the reply form, so the work is not lost.
                    fail();

                    return;
                }

                // The reply is live in the catalog from here on. Whatever follows only tidies up local
                // state, so it must never put the review back in line for "Retry failed".
                runRef.current.sentCount += 1;

                try {
                    refreshReviewWithAnswer(camelizeKeys(reply) as ReviewDataChildType, review.id);
                    setReviewAnswer(review.id, '');
                    stopActivity({
                        reviewId: review.id,
                        brandId: review.brand.id,
                        action: ReviewPresenceActionEnum.Reply,
                    });
                } catch {
                    // A stale card is harmless next to a reply published twice.
                }

                complete();
            }

            function handleGenerated() {
                runRef.current.generatedCount += 1;
                rememberGeneratedAnswer(review.id);

                // A cancelled run (the user pressed "Cancel" or left the page) keeps the text it already
                // generated in the form but publishes nothing more.
                if (runRef.current.mode === BulkAiGenerationModeEnum.GenerateAndSend && !runRef.current.isCancelled) {
                    send();

                    return;
                }

                complete();
            }

            // Drop whatever is in the form so the streamed chunks do not append to an old draft.
            setReviewAnswer(review.id, '');

            requestAIGenerate(review.id, {
                onComplete: handleGenerated,
                onError: (_reviewId, reason) => fail(reason),
            });
        },
        [
            featuresByBrand,
            getReviewAnswer,
            publishProgress,
            refreshReviewWithAnswer,
            rememberGeneratedAnswer,
            requestAIGenerate,
            setReviewAnswer,
            stopActivity,
        ]
    );

    const pump = useCallback(() => {
        if (runRef.current.isCancelled) {
            runRef.current.queue = [];
        }

        const freeSlots = Math.max(0, GENERATION_CONCURRENCY - runRef.current.activeCount);

        runRef.current.queue.splice(0, freeSlots).forEach((review) => runReview(review));

        if (runRef.current.queue.length === 0 && runRef.current.activeCount === 0) {
            finalize();
        }
    }, [finalize, runReview]);

    pumpRef.current = pump;

    const run = useCallback(
        (runReviews: Array<ReviewDataWithCatalogType>, mode: BulkAiGenerationModeEnum, isRetry = false) => {
            // Whatever a failed run left in the form is its own output, not a user draft, so drafts do
            // not hold a retry back.
            const context = isRetry ? {...getRunContext(mode), hasDraft: () => false} : getRunContext(mode);
            const eligibleReviews = runReviews.filter((review) => getBulkAiRunSkipReason(review, context) === null);

            runRef.current = {
                ...createInitialRunState(),
                mode,
                queue: eligibleReviews,
                totalCount: eligibleReviews.length,
                skippedReasons: countBulkAiSkipReasons(runReviews, context),
            };

            setSummary(null);
            publishProgress();
            pump();
        },
        [getRunContext, publishProgress, pump]
    );

    // Generations already in flight cannot be recalled; this only keeps them from being published.
    const stopRun = useCallback(() => {
        runRef.current.isCancelled = true;
        runRef.current.queue = [];
    }, []);

    const cancel = useCallback(() => {
        stopRun();

        // The summary waits for the generations still in flight.
        if (runRef.current.activeCount === 0) {
            finalize();
        }
    }, [finalize, stopRun]);

    // Leaving the page drops the queue and stops publishing what is still being generated.
    useEffect(() => stopRun, [stopRun]);

    const retryFailed = useCallback(() => {
        const {failedReviews, mode} = runRef.current;

        if (failedReviews.length === 0) {
            return;
        }

        // Re-read every review from the current list, so one answered in the meantime is skipped as
        // already answered instead of getting a second reply.
        const currentReviews = failedReviews.map((review) => {
            const currentReview = reviews?.results.find((item) => item.id === review.id);

            return currentReview ? {...review, ...currentReview} : review;
        });

        run(currentReviews, mode, true);
    }, [reviews, run]);

    const reset = useCallback(() => {
        runRef.current.failedReviews = [];
        setSummary(null);
        setProgress(null);
    }, []);

    return {
        progress,
        summary,
        isRunning: progress !== null,
        hasFailedReviews: runRef.current.failedReviews.length > 0,
        start: run,
        getEligibleCount,
        retryFailed,
        cancel,
        reset,
    };
}
